/**
 * Mixed-direction lines in Lit (v1 ticket 24): the interface's lines that
 * quote the target, isolated so both read right in either direction. The
 * rules live in `bidi.ts`.
 */
import { html, nothing } from 'lit';
import { currentLang, dirFor, t, targetDir, targetLangAttr } from './i18n';
import { backArrow, lineDir, onArrow, scriptRuns } from './bidi';

type Vars = Record<string, string | number>;

const rtlInterface = () => dirFor(currentLang()) === 'rtl';

/** The interface's direction, for a line that always opens in the interface's language. */
export function ui(): 'rtl' | 'ltr' {
  return rtlInterface() ? 'rtl' : 'ltr';
}

/** The direction of an interface line that may quote the target, or be all target (then `auto`). */
export function uiDir(text: string | null | undefined): 'rtl' | 'ltr' | 'auto' {
  return lineDir(text ?? '', rtlInterface());
}

/**
 * An interface line with each run in the other script (a Spanish quote in a
 * Persian line, a Persian word in an English one) in a `<bdi>` of its own
 * direction. A line in one script comes back as it is.
 */
export function iso(text: string | null | undefined) {
  if (!text) return text ?? '';
  const rtl = rtlInterface();
  const runs = scriptRuns(text, rtl);
  if (runs.length === 1 && !runs[0].other) return text;
  const tDir = targetDir();
  const lang = (rtl && tDir === 'ltr') || (!rtl && tDir === 'rtl') ? targetLangAttr() : undefined;
  return runs.map((r) => (r.other ? html`<bdi dir=${rtl ? 'ltr' : 'rtl'} lang=${lang ?? nothing}>${r.text}</bdi>` : r.text));
}

/** A string with one slot filled by a template part (an isolated answer), the rest as `t` fills it. */
export function tPart(key: string, slot: string, part: unknown, vars?: Vars) {
  const [before, after = ''] = t(key, { ...vars, [slot]: '\u0000' }).split('\u0000');
  return html`${before}${part}${after}`;
}

/** Target words listed in an interface line, each isolated in its own direction, with the interface's comma. */
export function targetList(words: readonly string[]) {
  return words.map((w, n) => html`${n ? t('common.sep') : ''}<bdi class="ca" dir="auto">${w}</bdi>`);
}

/** The back arrow, pointing the way the interface reads back. */
export function backGlyph(): string {
  return backArrow(rtlInterface());
}

/** An arrow from one thing to the next (a missed card to its answer), the way the interface reads. */
export function onGlyph(): string {
  return onArrow(rtlInterface());
}

/** A string with several slots filled by template parts (isolated titles), the rest as `t` fills it. */
export function tParts(key: string, parts: Record<string, unknown>, vars?: Vars) {
  const names = Object.keys(parts);
  const marks = Object.fromEntries(names.map((n, i) => [n, `\u0000${i}\u0000`]));
  const pieces = t(key, { ...vars, ...marks }).split('\u0000');
  // Odd pieces are slot indices, even ones the string between them.
  return html`${pieces.map((p, i) => (i % 2 ? parts[names[Number(p)]] : p))}`;
}
